import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { FormCascadeRule } from '../../../../config/formCascadeConfig';
import type { ApiPPG, PPGRow } from '../../../../types/promoMetrics';
import { cascadeKPIs } from '../../utils/cascadeKPIs';
import { applyFormCascadeToRows, computeSeedForRow } from '../../utils/applyFormCascade';
import { initializeFromApi } from '../../utils/promoStateUtils';

interface UsePpgDataArgs {
    existingPpgData: ApiPPG[];
    existingPpgIds: number[];
    fetchedPpgData: ApiPPG[];
    formCascadeRules: FormCascadeRule[];
    promoFormValues: Record<string, unknown>;
}

interface UsePpgDataReturn {
    ppgData: PPGRow[];
    hasProducts: boolean;
    baseline: Map<string, Record<string, number>>;
    activeExistingPpgIds: number[] | null;
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    applyFormCascade: (formValues: Record<string, unknown>) => void;
    onAddProducts: (ppgIds: number[], existingPpgIds: number[]) => { newIds: number[] };
    resetEditableSpendsToZero: () => void;
    resetPpgToExisting: (existingRows: ApiPPG[]) => void;
    clearPpgState: () => void;
    snapshotBaseline: () => void;
}

/** Builds a baseline snapshot from already-prepared rows (i.e. AFTER any seeding). */
const buildSnapshot = (rows: PPGRow[]): Map<string, Record<string, number>> => {
    const snapshot = new Map<string, Record<string, number>>();
    for (const row of rows) {
        snapshot.set(String(row.ppg_id), { ...row.values });
    }
    return snapshot;
};

const usePpgData = ({
    existingPpgData,
    existingPpgIds,
    fetchedPpgData,
    formCascadeRules,
    promoFormValues,
}: UsePpgDataArgs): UsePpgDataReturn => {
    const tpoGenConfig = useTpoGenConfig();
    const allConfigs = useMemo(
        () => [...tpoGenConfig.promoMetricsConfig, ...tpoGenConfig.promoSpendsConfig],
        [tpoGenConfig],
    );
    const editableSpendKeys = useMemo(
        () => tpoGenConfig.promoSpendsConfig.filter((c) => c.editable).map((c) => c.key),
        [tpoGenConfig],
    );

    const [ppgData, setPpgData] = useState<PPGRow[]>([]);
    const [hasProducts, setHasProducts] = useState(false);
    const [activeExistingPpgIds, setActiveExistingPpgIds] = useState<number[] | null>(null);

    const baselineRef = useRef<Map<string, Record<string, number>>>(new Map());

    // Latest rules + form values for the init effects, read at init time without
    // re-running the effects on every keystroke.
    const formCascadeRef = useRef({ rules: formCascadeRules, values: promoFormValues });
    formCascadeRef.current = { rules: formCascadeRules, values: promoFormValues };

    const handleMetricChange = useCallback((ppgId: string, metricKey: string, newValue: number) => {
        setPpgData((prev) =>
            prev.map((ppg) => {
                if (String(ppg.ppg_id) !== ppgId) return ppg;

                const { values, cascadedKeys } = cascadeKPIs(
                    allConfigs,
                    metricKey,
                    newValue,
                    ppg.values,
                );
                const newUserEdited = new Set(ppg.userEditedKeys);
                newUserEdited.add(metricKey);

                return {
                    ...ppg,
                    values,
                    userEditedKeys: newUserEdited,
                    lastCascadedKeys: cascadedKeys,
                };
            }),
        );
    }, [allConfigs]);

    /**
     * Change-triggered form -> metric cascade: seeds every current row (saved +
     * recommended) via the same `onMetricChange` path a manual edit uses, so the
     * seed is marked user-edited (dirty + saved) and cascades to dependents.
     */
    const applyFormCascade = useCallback(
        (formValues: Record<string, unknown>) => {
            if (formCascadeRules.length === 0) return;
            for (const ppg of ppgData) {
                for (const rule of formCascadeRules) {
                    const seed = computeSeedForRow(rule, ppg.values, formValues);
                    if (seed !== null) {
                        handleMetricChange(String(ppg.ppg_id), rule.effect.metricKey, seed);
                    }
                }
            }
        },
        [formCascadeRules, ppgData, handleMetricChange],
    );

    const resetEditableSpendsToZero = useCallback(() => {
        setPpgData((prev) =>
            prev.map((ppg) => {
                const resetValues = { ...ppg.values };
                const allResetKeys = new Set<string>();

                for (const key of editableSpendKeys) {
                    resetValues[key] = 0;
                    allResetKeys.add(key);
                }

                let finalValues = resetValues;
                const allCascaded = new Set<string>();
                for (const key of editableSpendKeys) {
                    const { values, cascadedKeys } = cascadeKPIs(
                        allConfigs,
                        key,
                        0,
                        finalValues,
                    );
                    finalValues = values;
                    cascadedKeys.forEach((k) => allCascaded.add(k));
                }

                return {
                    ...ppg,
                    values: finalValues,
                    userEditedKeys: new Set<string>(),
                    lastCascadedKeys: new Set([...allResetKeys, ...allCascaded]),
                };
            }),
        );
    }, [allConfigs, editableSpendKeys]);

    const handleAddProducts = useCallback(
        (ppgIds: number[], currentExistingPpgIds: number[]) => {
            if (ppgIds.length === 0) return { newIds: [] as number[] };
            const existingIdSet = new Set(currentExistingPpgIds);
            const newIds = ppgIds.filter((id) => !existingIdSet.has(id));
            const keptExistingIds = ppgIds.filter((id) => existingIdSet.has(id));

            setActiveExistingPpgIds(keptExistingIds);
            return { newIds };
        },
        [],
    );

    const snapshotBaseline = useCallback(() => {
        const snapshot = new Map<string, Record<string, number>>();
        for (const ppg of ppgData) {
            snapshot.set(String(ppg.ppg_id), { ...ppg.values });
        }
        baselineRef.current = snapshot;

        setActiveExistingPpgIds(null);

        setPpgData((prev) =>
            prev.map((ppg) => ({
                ...ppg,
                userEditedKeys: new Set<string>(),
                lastCascadedKeys: new Set<string>(),
            })),
        );
    }, [ppgData]);

    const resetPpgToExisting = useCallback((existingRows: ApiPPG[]) => {
        setActiveExistingPpgIds(null);

        if (existingRows.length > 0) {
            const { rows, snapshot } = initializeFromApi(existingRows);
            baselineRef.current = snapshot;
            setPpgData(rows);
        }
    }, []);

    const clearPpgState = useCallback(() => {
        setPpgData([]);
        setHasProducts(false);
        baselineRef.current = new Map();
        setActiveExistingPpgIds(null);
    }, []);

    useEffect(() => {
        if (existingPpgData.length > 0) {
            const { rows, snapshot } = initializeFromApi(existingPpgData);
            baselineRef.current = snapshot;
            setPpgData(rows);
            setHasProducts(true);
        }
    }, [existingPpgData]);

    useEffect(() => {
        const { rules, values } = formCascadeRef.current;

        if (activeExistingPpgIds === null) {
            if (fetchedPpgData.length > 0) {
                // Pure recommended rows: BE never saw the offer, so seed all of them.
                const { rows } = initializeFromApi(fetchedPpgData);
                const seededRows = applyFormCascadeToRows(rows, rules, values, allConfigs);
                baselineRef.current = buildSnapshot(seededRows);
                setPpgData(seededRows);
                setHasProducts(true);
            }
            return;
        }

        const existingIdSet = new Set(activeExistingPpgIds);
        const keptExistingRows = existingPpgData.filter((ppg) => existingIdSet.has(ppg.ppg_id));
        const allApiPpgs = [...keptExistingRows, ...fetchedPpgData];

        if (allApiPpgs.length > 0) {
            const { rows } = initializeFromApi(allApiPpgs);
            // Seed only the newly-fetched rows; kept saved rows keep their BE values.
            const fetchedIdSet = new Set(fetchedPpgData.map((p) => String(p.ppg_id)));
            const seededRows = applyFormCascadeToRows(rows, rules, values, allConfigs, {
                onlyPpgIds: fetchedIdSet,
            });
            baselineRef.current = buildSnapshot(seededRows);
            setPpgData(seededRows);
            setHasProducts(true);
        } else {
            setPpgData([]);
            setHasProducts(false);
            baselineRef.current = new Map();
        }
    }, [activeExistingPpgIds, fetchedPpgData, existingPpgData, allConfigs]);

    return {
        ppgData,
        hasProducts,
        baseline: baselineRef.current,
        activeExistingPpgIds,
        onMetricChange: handleMetricChange,
        applyFormCascade,
        onAddProducts: handleAddProducts,
        resetEditableSpendsToZero,
        resetPpgToExisting,
        clearPpgState,
        snapshotBaseline,
    };
};

export default usePpgData;
